<template>
  <BigContainer>
  <div class="mx-auto max-w-5xl px-2 sm:px-4 lg:px-6">
    <PageHeader
      title="The Jesse blog"
      description="Tutorials, updates, and ideas on algorithmic trading—written for quants."
      eyebrow="Learn and explore"
      icon="i-heroicons-newspaper"
      centered
    />

    <SurfaceWell class="mt-10">
    <div class="space-y-3">
    <NuxtLink
      v-if="featured"
      :href="`/blog/${featured.category.slug}/${featured.slug}`"
      class="group block rounded-xl border border-gray-200 bg-white p-8 shadow-xs hover:border-amber-400 dark:border-stone-700 dark:bg-stone-900 dark:hover:border-amber-500 sm:p-10">
      <div class="flex flex-wrap items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
        <span class="rounded-full bg-amber-100 dark:bg-amber-400/15 px-2.5 py-0.5 font-semibold uppercase tracking-widest text-amber-700 dark:text-amber-400">
          Latest
        </span>
        <span class="font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400">{{ featured.category.name }}</span>
        <span class="text-gray-300 dark:text-gray-600">&bull;</span>
        <time :datetime="String(featured.created_at)">{{ formatDate(featured.created_at, false) }}</time>
        <span class="text-gray-300 dark:text-gray-600">&bull;</span>
        <span>{{ blogReadingTime(featured.body) }} min read</span>
      </div>
      <h2 class="mt-4 font-serif font-semibold text-3xl sm:text-4xl leading-tight tracking-tight text-gray-900 dark:text-gray-100 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
        {{ featured.title }}
      </h2>
      <p class="mt-4 text-base sm:text-lg leading-relaxed text-gray-600 dark:text-gray-400 line-clamp-3 max-w-3xl">
        {{ blogExcerpt(featured.description, featured.body, 60) }}
      </p>
      <span class="mt-5 inline-flex items-center text-sm font-medium text-amber-600 dark:text-amber-400">
        Read more <span class="ml-1" aria-hidden="true">&rarr;</span>
      </span>
    </NuxtLink>

    <div v-if="rest.length" class="grid gap-3 sm:grid-cols-2">
      <BlogCard v-for="article in rest" :key="article.slug" :article="article" />
    </div>

    <SectionCard v-if="!response.data.length">
      <div class="py-12 text-center text-gray-500 dark:text-gray-400">
        No articles yet—check back soon.
      </div>
    </SectionCard>
    </div>
    </SurfaceWell>

    <div v-if="response.meta.last_page > 1" class="mt-12">
      <ContentPagination
        v-model:page="currentPage"
        :total="response.meta.total"
        :per-page="response.meta.per_page"
        item-label="articles"
      />
    </div>
  </div>
  </BigContainer>
</template>


<script setup lang="ts">
import { setSeoMeta } from "~/utils/meta";
import { formatDate } from "~/utils/date";
import { blogExcerpt, blogReadingTime } from "~/utils/blog";

const seoTitle = 'Jesse Blog'
const seoDescription = 'Get the latest news, updates, and tutorials on Jesse, the open-source Python trading framework for cryptocurrencies.'
setSeoMeta(seoTitle, seoDescription, null, 'article')

const path = '/blog'
const route = useRoute()
const currentPage = route.query.page ? ref(parseInt(route.query.page as string)) : ref(1)
const { data: initialResponse } = await useSetupApi2<BlogResponse>(`/blog?page=${currentPage.value}`)
const response = ref(initialResponse.value as BlogResponse)

// On page 1 the newest post is featured; the rest go in the grid. On later pages, everything is a grid.
const featured = computed(() => (currentPage.value === 1 && response.value.data.length ? response.value.data[0] : null))
const rest = computed(() => {
  const data = response.value.data
  return currentPage.value === 1 ? data.slice(1) : data
})

watch(currentPage, async (page) => {
  const { data, error } = await useApi2<BlogResponse>(`/blog?page=${page}`)
  if (error.value) throw error.value
  response.value = data.value as BlogResponse
  if (currentPage.value > 1) {
    navigateTo({ path: path, query: { page: currentPage.value } })
  } else {
    navigateTo({ path: path })
  }
})
</script>
